import { ChangeDetectionStrategy, Component, forwardRef, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { combineLatest, filter, map, Subscription } from 'rxjs';
import { AuthService } from '../../../auth/auth.service';
import { BorderboxResizeObserverDirective } from '../../../resize-observer/border-box-resize-observer.directive';
import { FullScreenModeDirective } from '../../../shared/directives/full-screen-mode/full-screen-mode.directive';
import { SharedModule } from '../../../shared/shared.module';
import { QueryParams } from '../../enums/query-params.enum';
import { QueryParamsService } from '../../services/query-params/query-params.service';
import { isMobile } from '../../util/feature-detection.helper';
import { BurgerMenuComponent } from '../mobile-navbar/burger-menu/burger-menu.component';
import { MobileNavbarComponent } from '../mobile-navbar/mobile-navbar.component';
import { NavbarComponent } from '../navbar/navbar.component';
import { SimpleNavbarComponent } from '../simple-navbar/simple-navbar.component';
import { SubNavbarComponent } from '../sub-navbar/sub-navbar.component';
import { ThirdNavbarComponent } from '../third-navbar/third-navbar.component';
import { TreeService } from '../tree.service';

export function getCurrentParams(activatedRoute: ActivatedRoute) {
  return QueryParamsService.getAllowedQueryParams(activatedRoute, [QueryParams.VIEW, QueryParams.SALE_MODE]);
}

const DISABLE_SCROLL = 'disable-scroll';

@Component({
  selector: 'app-navbar-tree',
  templateUrl: './navbar-tree.component.html',
  styleUrls: ['./navbar-tree.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SharedModule,
    FullScreenModeDirective,
    BorderboxResizeObserverDirective,
    NavbarComponent,
    SubNavbarComponent,
    forwardRef(() => ThirdNavbarComponent),
    MobileNavbarComponent,
    BurgerMenuComponent,
    SimpleNavbarComponent,
  ],
})
export class NavbarTreeComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private authService = inject(AuthService);
  private treeService = inject(TreeService);

  private subscription = new Subscription();
  authenticated: boolean;
  isThirdNavbarOpen: boolean;
  isBurgerMenuOpen: boolean;

  isMobile = false;

  showSimpleNav$ = combineLatest([
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() =>
        this.router.isActive('/terms', {
          paths: 'exact',
          queryParams: 'ignored',
          fragment: 'ignored',
          matrixParams: 'ignored',
        }),
      ),
    ),
    this.authService.authenticatedObservable(),
  ]).pipe(map(([matchesTermsUrl, loggedIn]) => matchesTermsUrl && !loggedIn));

  ngOnInit(): void {
    this.isMobile = isMobile();

    this.subscription.add(
      this.authService.authenticatedObservable().subscribe((auth) => {
        this.authenticated = auth;
      }),
    );
    this.subscription.add(
      this.treeService.getState().subscribe(({ second, third }) => {
        this.isThirdNavbarOpen = !!second && third;
        if (this.isThirdNavbarOpen) {
          document.querySelector('body').classList.add(DISABLE_SCROLL);
        } else {
          document.querySelector('body').classList.remove(DISABLE_SCROLL);
        }
      }),
    );

    this.subscription.add(
      this.treeService.getBurgerMenuOpenedAsObservable().subscribe((opened) => {
        this.isBurgerMenuOpen = opened;
      }),
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}
